import { gsap } from "gsap";
import { Text } from "pixi.js";

import { AbstractBitmapText } from "../../../../../abstracts/graphics/AbstractBitmapText";
import { AbstractSpine } from "../../../../../abstracts/graphics/AbstractSpine";
import { AbstractView } from "../../../../../abstracts/modules/AbstractView";
import { fitDisplayObjectToWidth } from "../../../../../abstracts/utils/GraphicUtils";
import {
    fadeAndStopSound,
    playNextBackgroundMusic,
    playSound,
    startAmbSounds,
    stopBackgroundMusic,
} from "../../../../../abstracts/utils/SoundUtils";
import { DDD } from "../../../../DDD";
import { DDDFontsNames, DDDSoundNames } from "../../../../assetsLoader/DDDAssetsNames";
import { DDDFreespinIntroScreen } from "./DDDFreespinIntroScreen";

export class DDDFreespinOutroScreen extends DDDFreespinIntroScreen {
    private totalWinText: AbstractBitmapText;
    private spinsPlayedText: Text;

    constructor(parentContainer: AbstractView, name: string = `DDDFreespinOutroScreen`) {
        super(parentContainer, name);
    }

    protected override addBackgroundImage() {
        this.backgroundSpine = new AbstractSpine(this.getSpineName());
        this.backgroundSpine.scale.set(0.5);
        this.backgroundSpine.name = `bgSpine`;

        this.addChild(this.backgroundSpine);
    }

    protected override getTweenName(): string {
        return `deadspinoutro`;
    }

    protected continueButtonClickCallback() {
        this.onContinueBtnClick();
        this.selfRemoveFromParent();

        this.backgroundSpine?.reset();

        this.removeChild(this.continueButton);

        playSound({ name: DDDSoundNames.CONTINUE_BUTTON });
        startAmbSounds();

        fadeAndStopSound({ name: this.getIntroLoopSoundName(), duration: 0.5, from: 1, to: 0 });

        this.removeChild(this.spinsPlayedText);
        this.removeChild(this.totalWinText);
    }

    protected override init(): void {
        super.init();

        this.totalWinText = new AbstractBitmapText({
            text: `0`,
            style: {
                fontName: DDDFontsNames.DEFAULT,
                fontSize: 16,
                tint: 0xffffff,
            },
        });
        this.totalWinText.name = `totalWinText`;
        this.totalWinText.alpha = 0.9;

        this.spinsPlayedText = new Text(`0`, {
            fontFamily: `Arial`,
            fontSize: 36,
            fill: 0xc2beb4,
            stroke: 0x000000,
            strokeThickness: 2,
        });
        this.spinsPlayedText.name = `spinsPlayedText`;
        this.spinsPlayedText.anchor.set(0.5);
        this.spinsPlayedText.position.set(0, 138);
    }

    public override getGsapAnimation(
        finalWinValue: number = 0,
        shouldPlaySound: boolean = true
    ): gsap.core.Timeline {
        if (shouldPlaySound) {
            if (finalWinValue > 0) {
                playSound({ name: this.getOutroWinSoundName() });
            }
        }

        if (finalWinValue === 0) {
            playSound({ name: DDDSoundNames.FS_OUTRO_BELOW_STAKE });
        }

        const spinPlayed: number = DDD.model.gameData.freespinsPlayed;

        this.spinsPlayedText.text =
            DDD.apiPlugin.translations.translate(`Spins played: `) + `${spinPlayed}`;

        const tl = gsap.timeline({
            autoRemoveChildren: true,
            onComplete: () => {
                this.addChild(this.continueButton);
                this.addChild(this.spinsPlayedText);

                stopBackgroundMusic();
                playNextBackgroundMusic();
            },
        });

        const finalValueToString: string = finalWinValue === 0 ? `0.00` : finalWinValue.toFixed(2);

        const showScreenTween = gsap.fromTo(
            this,
            { alpha: 0 },
            {
                alpha: 1,
                duration: 0.3,
                onStart: () => {
                    this.totalWinText.updateValue(finalValueToString);
                    fitDisplayObjectToWidth(this.totalWinText, 700);
                    this.addChild(this.totalWinText);

                    fadeAndStopSound({ name: this.getBackgroundMusicName(), to: 0, duration: 0.5 });

                    this.backgroundSpine.getSpineTween(this.getTweenName());
                },
            }
        );

        tl.add(showScreenTween);

        if (finalWinValue > 0) {
            this.totalWinText.updateValue(finalValueToString);
            fitDisplayObjectToWidth(this.totalWinText, 700);
        }

        return tl;
    }

    protected getBackgroundMusicName(): string {
        return DDDSoundNames.FREESPIN_MUSIC;
    }

    protected getOutroWinSoundName(): string {
        return DDDSoundNames.FS_OUTRO_WIN;
    }
}
